// utils/createGraphQLThunk.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import { message } from "antd";
import { logoutAndResetStore } from "../slices/authSlice";

// JSON-based GraphQL thunk
export const createGraphQLThunk = ({
  query,
  getVariables = () => ({}),
  responseKey,
  onSuccessMessage,
  url,
  notError,
}) => {
  return async (args, { getState, rejectWithValue, dispatch }) => {
    const state = getState();
    const accessToken = state.auth.token;

    try {
      const response = await fetch(url || process.env.REACT_APP_VENDOR, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          query,
          variables: getVariables(args),
        }),
      });

      const json = await response.json();

      if (response.status === 401) {
        dispatch(logoutAndResetStore()); // ⬅️ Clears auth and any other slices
        message.error("Someone logged-in from another device.");
        return rejectWithValue("Unauthorized. Please login again.");
      }

      if (json.errors) {
        const errorMsg = json.errors[0].message;
        if (!notError) message.error(errorMsg);
        return rejectWithValue(errorMsg);
      }


      //       if (response?.status === 401) {
      //   dispatch(logoutAndResetStore());
      //   localStorage.clear();
      //   sessionStorage.clear();
      //   message.error("Someone logged-in from another device.");
      //   return rejectWithValue("Unauthorized. Please login again.");
      // }

      // if (json.errors) {
      //   const errorMsg = json.errors[0].message;
      //   // 🔹 handle GraphQL unauthorized here
      //   if (errorMsg.toLowerCase().includes("unauthorized")) {
      //     dispatch(logoutAndResetStore());
      //     localStorage.clear();
      //     sessionStorage.clear();
      //     message.error("Unauthorized. Please login again.");
      //   } else if (!notError) {
      //     message.error(errorMsg);
      //   }
      //   return rejectWithValue(errorMsg);
      // }


      
      const result = responseKey ? json.data?.[responseKey] : json.data;

      if (onSuccessMessage) {
        const successMsg = typeof onSuccessMessage === "function"
          ? onSuccessMessage(args)
          : onSuccessMessage;

        message.success(successMsg);
      }

      return result;
    } catch (error) {
      if (!notError) message.error(error.message);
      return rejectWithValue(error.message);
    }
  };
};

// Multipart (file-upload) GraphQL thunk
export const createMultipartGraphQLThunk = ({
  query,
  getVariables,
  fileKey = "image",          // Legacy single file support
  fileKeys = null,           // New multi-field support
  responseKey,
  onSuccessMessage,
  url,
  notError,
}) => {
  return async (args, { getState, rejectWithValue }) => {
    const state = getState();
    const accessToken = state.auth.token;

    const formData = new FormData();
    const variables = getVariables({ ...args });
    const map = {};
    let fileIndex = 0;

    const getValueAtPath = (obj, path) =>
      path.split(".").reduce((acc, part) => acc?.[part], obj);

    const setValueAtPath = (obj, path, value) => {
      const parts = path.split(".");
      const last = parts.pop();
      const target = parts.reduce((acc, part) => {
        if (!acc[part]) acc[part] = {};
        return acc[part];
      }, obj);
      target[last] = value;
    };

    // Backward-compatible single file upload
    if (!fileKeys) {
      const { [fileKey]: file, ...rest } = args;
      Object.assign(variables, getVariables(rest));
      if (file instanceof File) {
        variables[fileKey] = null;
        map[fileIndex] = [`variables.${fileKey}`];
        formData.append(`${fileIndex}`, file);
      }
    } else {
      // Modern multi-file/multi-field upload
      for (const keyPath of fileKeys) {
        if (keyPath.endsWith("[]")) {
          // Handle array of files
          const arrayPath = keyPath.replace("[]", "");
          const files = getValueAtPath(args, arrayPath);

          if (Array.isArray(files)) {
            const updatedArray = files.map((file, i) => {
              if (file instanceof File) {
                map[fileIndex] = [`variables.${arrayPath}.${i}`];
                formData.append(`${fileIndex}`, file);
                fileIndex++; // ✅ increment here for each file
                return null; // GraphQL spec requires null where the file was
              }
              return file;
            });

            setValueAtPath(variables, arrayPath, updatedArray);
          }
        } else if (keyPath.includes("[].")) {
          const [arrayPath, nestedPath] = keyPath.split("[].");
          const array = getValueAtPath(args, arrayPath);
          if (Array.isArray(array)) {
            const updatedArray = array.map((item, i) => {
              const file = getValueAtPath(item, nestedPath);
              if (file instanceof File) {
                map[fileIndex] = [`variables.${arrayPath}.${i}.${nestedPath}`];
                formData.append(`${fileIndex}`, file);
                const newItem = { ...item };
                setValueAtPath(newItem, nestedPath, null);
                fileIndex++;
                return newItem;
              }
              return item;
            });
            setValueAtPath(variables, arrayPath, updatedArray);
          }
        } else {
          const file = getValueAtPath(args, keyPath);
          if (file instanceof File) {
            setValueAtPath(variables, keyPath, null);
            map[fileIndex] = [`variables.${keyPath}`];
            formData.append(`${fileIndex}`, file);
            fileIndex++;
          }
        }
      }
    }
    formData.append("operations", JSON.stringify({ query, variables }));
    formData.append("map", JSON.stringify(map));

    try {
      const response = await fetch(url || process.env.REACT_APP_VENDOR, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      const json = await response.json();
      const result = responseKey ? json.data?.[responseKey] : json.data;

      if (json.errors) {
        console.log("internal error", json.errors);
        const errorMsg = json.errors[0].message;
        if (!notError) message.error(errorMsg);
        return rejectWithValue(errorMsg);
      }

      else if (onSuccessMessage) {
        const successMsg = result && typeof onSuccessMessage === "function"
          ? onSuccessMessage(args)
          : onSuccessMessage;

        message.success(successMsg);
      }

      return result;
    } catch (error) {
      const errMsg = error?.message || "Something went wrong";
      console.log("outer error", errMsg);
      if (!notError) message.error(errMsg);
      return rejectWithValue(errMsg);
    }
  };
};

export const createContentThunk = ({
  typePrefix,
  query,
  getVariables = () => ({}),
  responseKey,
  onSuccessMessage,
}) =>
  createAsyncThunk(typePrefix, async (args, { getState, rejectWithValue, dispatch }) => {
    const token = getState().auth.token;

    try {
      const response = await fetch(process.env.REACT_APP_VENDOR, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          query,
          variables: getVariables(args),
        }),
      });

      const json = await response.json();
      if (response.status === 401) {
        dispatch(logoutAndResetStore()); // ⬅️ Clears auth and any other slices
        message.error("Someone logged-in from another device.");
        return rejectWithValue("Unauthorized. Please login again.");
      }
      if (json.errors) {
        const errorMsg = json.errors[0].message;
        message.error(errorMsg);
        return rejectWithValue({
          message: json.errors[0]?.message || "Unknown GraphQL error",
          contentType: args.contentType,
        });
      }

      if (onSuccessMessage) {
        message.success(onSuccessMessage);
      }

      return {
        data: responseKey ? json.data?.[responseKey] : json.data,
        contentType: args.contentType,
      };
    } catch (error) {
      return rejectWithValue({
        message: error.message || "Network error",
        contentType: args.contentType,
      });
    }
  });
